import React, { useState } from 'react';
import { Customized } from 'recharts';
import { calcFibLevels, calcFibExtensions } from '../utils/chartDrawings';

// Theme-aware colors
const COLORS = {
  fib: { line: '#3b82f6', text: '#3b82f6', levels: ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#64748b'] },
  trendline: '#f59e0b',
  hline: '#22c55e',
  arrow: '#ec4899',
  ray: '#06b6d4',
  rect: { fill: 'rgba(59, 130, 246, 0.08)', stroke: '#3b82f6' },
  channel: { fill: 'rgba(168, 85, 247, 0.06)', stroke: '#a855f7', mid: '#c084fc' },
  hrange: { fill: 'rgba(249, 115, 22, 0.10)', stroke: '#f97316' },
  ellipse: { fill: 'rgba(236, 72, 153, 0.08)', stroke: '#ec4899' },
  text: '#64748b',
};

// Hit-area padding for tap/click detection (pixels)
const HIT_PAD = 8;

/**
 * Renders drawings as SVG inside a Recharts chart via <Customized>.
 *
 * Each drawing is stored as:
 *   { id, type, points: [{x, y}, ...], meta? }
 *
 * The component converts data-space coordinates to pixel-space
 * using the chart's xAxis/yAxis scales provided by Customized.
 *
 * Tap/click a drawing to select it (mobile-friendly).
 * Shift+click to delete directly (desktop shortcut).
 */
export default function ChartDrawings({ drawings, onDelete, onSelect, selectedId }) {
  if (!drawings?.length) return null;

  return (
    <Customized
      component={(props) => (
        <DrawingRenderer
          {...props}
          drawings={drawings}
          onDelete={onDelete}
          onSelect={onSelect}
          selectedId={selectedId}
        />
      )}
    />
  );
}

function DrawingRenderer({ drawings, onDelete, onSelect, selectedId, xAxisMap, yAxisMap, offset }) {
  const xAxis = xAxisMap && Object.values(xAxisMap)[0];
  const yAxis = yAxisMap && Object.values(yAxisMap)[0];

  if (!xAxis?.scale || !yAxis?.scale) return null;

  const xScale = xAxis.scale;
  const yScale = yAxis.scale;

  const toPixel = (dataPoint) => ({
    px: xScale(dataPoint.x) + (offset?.left || 0),
    py: yScale(dataPoint.y) + (offset?.top || 0),
  });

  const handleDrawingClick = (e, drawing) => {
    e.stopPropagation();
    if (e.shiftKey) {
      onDelete?.(drawing.id);
    } else {
      onSelect?.(drawing.id === selectedId ? null : drawing.id);
    }
  };

  return (
    <g className="chart-drawings">
      {drawings.map((drawing) => {
        const isSelected = drawing.id === selectedId;
        switch (drawing.type) {
          case 'fib':
            return <FibDrawing key={drawing.id} drawing={drawing} toPixel={toPixel} xScale={xScale} offset={offset} isSelected={isSelected} onClick={(e) => handleDrawingClick(e, drawing)} />;
          case 'hline':
            return <HLineDrawing key={drawing.id} drawing={drawing} toPixel={toPixel} offset={offset} isSelected={isSelected} onClick={(e) => handleDrawingClick(e, drawing)} />;
          case 'trendline':
            return <TrendLineDrawing key={drawing.id} drawing={drawing} toPixel={toPixel} isSelected={isSelected} onClick={(e) => handleDrawingClick(e, drawing)} />;
          case 'ray':
            return <RayDrawing key={drawing.id} drawing={drawing} toPixel={toPixel} offset={offset} isSelected={isSelected} onClick={(e) => handleDrawingClick(e, drawing)} />;
          case 'arrow':
            return <ArrowDrawing key={drawing.id} drawing={drawing} toPixel={toPixel} isSelected={isSelected} onClick={(e) => handleDrawingClick(e, drawing)} />;
          case 'rect':
            return <RectDrawing key={drawing.id} drawing={drawing} toPixel={toPixel} isSelected={isSelected} onClick={(e) => handleDrawingClick(e, drawing)} />;
          case 'channel':
            return <ChannelDrawing key={drawing.id} drawing={drawing} toPixel={toPixel} offset={offset} isSelected={isSelected} onClick={(e) => handleDrawingClick(e, drawing)} />;
          case 'hrange':
            return <HRangeDrawing key={drawing.id} drawing={drawing} toPixel={toPixel} offset={offset} isSelected={isSelected} onClick={(e) => handleDrawingClick(e, drawing)} />;
          case 'ellipse':
            return <EllipseDrawing key={drawing.id} drawing={drawing} toPixel={toPixel} isSelected={isSelected} onClick={(e) => handleDrawingClick(e, drawing)} />;
          case 'text':
            return <TextDrawing key={drawing.id} drawing={drawing} toPixel={toPixel} isSelected={isSelected} onClick={(e) => handleDrawingClick(e, drawing)} />;
          default:
            return null;
        }
      })}
    </g>
  );
}

/* ---------- Shared selection highlight ---------- */

function SelectionOutline({ children, isSelected }) {
  if (!isSelected) return <>{children}</>;
  return (
    <g filter="url(#selectionGlow)">
      {children}
    </g>
  );
}

function SelectionDefs() {
  return (
    <defs>
      <filter id="selectionGlow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor="#3b82f6" floodOpacity="0.6" />
      </filter>
    </defs>
  );
}

/* ---------- Individual drawing renderers ---------- */

function FibDrawing({ drawing, toPixel, xScale, offset, isSelected, onClick }) {
  const pts = drawing.points;
  if (pts.length < 2) return null;

  const p1 = toPixel(pts[0]);
  const p2 = toPixel(pts[1]);
  const fibLevels = calcFibLevels(pts[0].y, pts[1].y);
  const extensions = calcFibExtensions(pts[0].y, pts[1].y);

  const chartLeft = offset?.left || 0;
  const chartRight = (offset?.left || 0) + (offset?.width || 800);

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      {isSelected && <SelectionDefs />}
      <SelectionOutline isSelected={isSelected}>
        {/* Main vertical line */}
        <line x1={p1.px} y1={p1.py} x2={p2.px} y2={p2.py} stroke={COLORS.fib.line} strokeWidth={1.5} strokeDasharray="4 2" />

        {/* Retracement levels */}
        {fibLevels.map((fl, i) => {
          const py = p2.py + (p1.py - p2.py) * fl.level;
          return (
            <g key={`fib-${fl.level}`}>
              <line
                x1={chartLeft} y1={py} x2={chartRight} y2={py}
                stroke={COLORS.fib.levels[i] || COLORS.fib.line}
                strokeWidth={1}
                strokeDasharray={fl.level === 0 || fl.level === 1 ? "0" : "6 3"}
                opacity={0.7}
              />
              <text
                x={chartRight - 4} y={py - 3}
                textAnchor="end"
                fill={COLORS.fib.levels[i] || COLORS.fib.text}
                fontSize={9}
                fontWeight={600}
                className="select-none"
              >
                {fl.level.toFixed(3)} — {fl.price.toFixed(2)}
              </text>
            </g>
          );
        })}

        {/* Extension levels */}
        {extensions.map((ext) => {
          const py = p2.py + (p1.py - p2.py) * ext.level;
          return (
            <g key={`ext-${ext.level}`}>
              <line
                x1={chartLeft} y1={py} x2={chartRight} y2={py}
                stroke={COLORS.fib.line}
                strokeWidth={0.5}
                strokeDasharray="2 4"
                opacity={0.4}
              />
              <text
                x={chartRight - 4} y={py - 3}
                textAnchor="end"
                fill={COLORS.fib.text}
                fontSize={8}
                opacity={0.6}
                className="select-none"
              >
                {ext.level.toFixed(3)}
              </text>
            </g>
          );
        })}
      </SelectionOutline>

      {/* Label */}
      <text x={p1.px + 4} y={p1.py - 6} fill={COLORS.fib.text} fontSize={8} opacity={0.5} className="select-none">
        Fib
      </text>
    </g>
  );
}

function HLineDrawing({ drawing, toPixel, offset, isSelected, onClick }) {
  const pt = drawing.points[0];
  if (!pt) return null;

  const { py } = toPixel(pt);
  const chartLeft = offset?.left || 0;
  const chartRight = (offset?.left || 0) + (offset?.width || 800);

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      {isSelected && <SelectionDefs />}
      <SelectionOutline isSelected={isSelected}>
        {/* Invisible hit area for easier tapping */}
        <line x1={chartLeft} y1={py} x2={chartRight} y2={py} stroke="transparent" strokeWidth={HIT_PAD * 2} />
        <line
          x1={chartLeft} y1={py} x2={chartRight} y2={py}
          stroke={COLORS.hline}
          strokeWidth={1.5}
          strokeDasharray="8 4"
        />
        <rect x={chartLeft + 4} y={py - 9} width={60} height={16} rx={3} fill={COLORS.hline} opacity={0.9} />
        <text x={chartLeft + 8} y={py + 3} fill="white" fontSize={9} fontWeight={600} className="select-none">
          {pt.y.toFixed(2)}
        </text>
      </SelectionOutline>
    </g>
  );
}

function TrendLineDrawing({ drawing, toPixel, isSelected, onClick }) {
  const pts = drawing.points;
  if (pts.length < 2) return null;

  const p1 = toPixel(pts[0]);
  const p2 = toPixel(pts[1]);

  // Extend the line beyond the two points
  const dx = p2.px - p1.px;
  const dy = p2.py - p1.py;
  const ext = 3;
  const ex1 = p1.px - dx * ext;
  const ey1 = p1.py - dy * ext;
  const ex2 = p2.px + dx * ext;
  const ey2 = p2.py + dy * ext;

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      {isSelected && <SelectionDefs />}
      <SelectionOutline isSelected={isSelected}>
        {/* Invisible hit area */}
        <line x1={ex1} y1={ey1} x2={ex2} y2={ey2} stroke="transparent" strokeWidth={HIT_PAD * 2} />
        <line x1={ex1} y1={ey1} x2={ex2} y2={ey2} stroke={COLORS.trendline} strokeWidth={1.5} />
        <circle cx={p1.px} cy={p1.py} r={3} fill={COLORS.trendline} />
        <circle cx={p2.px} cy={p2.py} r={3} fill={COLORS.trendline} />
      </SelectionOutline>
    </g>
  );
}

function RayDrawing({ drawing, toPixel, offset, isSelected, onClick }) {
  const pts = drawing.points;
  if (pts.length < 2) return null;

  const p1 = toPixel(pts[0]);
  const p2 = toPixel(pts[1]);

  // Ray extends from p1 through p2 and beyond (one direction only)
  const dx = p2.px - p1.px;
  const dy = p2.py - p1.py;
  const ext = 10;
  const ex = p2.px + dx * ext;
  const ey = p2.py + dy * ext;

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      {isSelected && <SelectionDefs />}
      <SelectionOutline isSelected={isSelected}>
        {/* Invisible hit area */}
        <line x1={p1.px} y1={p1.py} x2={ex} y2={ey} stroke="transparent" strokeWidth={HIT_PAD * 2} />
        <line x1={p1.px} y1={p1.py} x2={ex} y2={ey} stroke={COLORS.ray} strokeWidth={1.5} />
        <circle cx={p1.px} cy={p1.py} r={3} fill={COLORS.ray} />
        <circle cx={p2.px} cy={p2.py} r={2.5} fill="none" stroke={COLORS.ray} strokeWidth={1} />
      </SelectionOutline>
    </g>
  );
}

function ArrowDrawing({ drawing, toPixel, isSelected, onClick }) {
  const pts = drawing.points;
  if (pts.length < 2) return null;

  const p1 = toPixel(pts[0]);
  const p2 = toPixel(pts[1]);

  const angle = Math.atan2(p2.py - p1.py, p2.px - p1.px);
  const headLen = 12;
  const a1x = p2.px - headLen * Math.cos(angle - Math.PI / 6);
  const a1y = p2.py - headLen * Math.sin(angle - Math.PI / 6);
  const a2x = p2.px - headLen * Math.cos(angle + Math.PI / 6);
  const a2y = p2.py - headLen * Math.sin(angle + Math.PI / 6);

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      {isSelected && <SelectionDefs />}
      <SelectionOutline isSelected={isSelected}>
        {/* Invisible hit area */}
        <line x1={p1.px} y1={p1.py} x2={p2.px} y2={p2.py} stroke="transparent" strokeWidth={HIT_PAD * 2} />
        <line x1={p1.px} y1={p1.py} x2={p2.px} y2={p2.py} stroke={COLORS.arrow} strokeWidth={2} />
        <polygon points={`${p2.px},${p2.py} ${a1x},${a1y} ${a2x},${a2y}`} fill={COLORS.arrow} />
      </SelectionOutline>
    </g>
  );
}

function RectDrawing({ drawing, toPixel, isSelected, onClick }) {
  const pts = drawing.points;
  if (pts.length < 2) return null;

  const p1 = toPixel(pts[0]);
  const p2 = toPixel(pts[1]);

  const x = Math.min(p1.px, p2.px);
  const y = Math.min(p1.py, p2.py);
  const w = Math.abs(p2.px - p1.px);
  const h = Math.abs(p2.py - p1.py);

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      {isSelected && <SelectionDefs />}
      <SelectionOutline isSelected={isSelected}>
        <rect
          x={x} y={y} width={w} height={h}
          fill={COLORS.rect.fill}
          stroke={COLORS.rect.stroke}
          strokeWidth={1.5}
          rx={2}
        />
      </SelectionOutline>
    </g>
  );
}

function ChannelDrawing({ drawing, toPixel, offset, isSelected, onClick }) {
  const pts = drawing.points;
  if (pts.length < 2) return null;

  const p1 = toPixel(pts[0]);
  const p2 = toPixel(pts[1]);

  // Channel: two parallel lines. The offset is perpendicular to the line direction.
  const dx = p2.px - p1.px;
  const dy = p2.py - p1.py;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  // Perpendicular unit vector
  const nx = -dy / len;
  const ny = dx / len;
  const channelWidth = Math.min(80, len * 0.4);

  // Top line
  const t1x = p1.px + nx * channelWidth;
  const t1y = p1.py + ny * channelWidth;
  const t2x = p2.px + nx * channelWidth;
  const t2y = p2.py + ny * channelWidth;

  // Bottom line
  const b1x = p1.px - nx * channelWidth;
  const b1y = p1.py - ny * channelWidth;
  const b2x = p2.px - nx * channelWidth;
  const b2y = p2.py - ny * channelWidth;

  // Extend all lines
  const ext = 3;
  const edx = dx * ext;
  const edy = dy * ext;

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      {isSelected && <SelectionDefs />}
      <SelectionOutline isSelected={isSelected}>
        {/* Fill between lines */}
        <polygon
          points={`${t1x - edx},${t1y - edy} ${t2x + edx},${t2y + edy} ${b2x + edx},${b2y + edy} ${b1x - edx},${b1y - edy}`}
          fill={COLORS.channel.fill}
        />
        {/* Top line */}
        <line x1={t1x - edx} y1={t1y - edy} x2={t2x + edx} y2={t2y + edy} stroke={COLORS.channel.stroke} strokeWidth={1.5} />
        {/* Bottom line */}
        <line x1={b1x - edx} y1={b1y - edy} x2={b2x + edx} y2={b2y + edy} stroke={COLORS.channel.stroke} strokeWidth={1.5} />
        {/* Mid line (dashed) */}
        <line x1={p1.px - edx} y1={p1.py - edy} x2={p2.px + edx} y2={p2.py + edy} stroke={COLORS.channel.mid} strokeWidth={0.8} strokeDasharray="4 3" opacity={0.5} />
        {/* Endpoints */}
        <circle cx={p1.px} cy={p1.py} r={3} fill={COLORS.channel.stroke} />
        <circle cx={p2.px} cy={p2.py} r={3} fill={COLORS.channel.stroke} />
      </SelectionOutline>
    </g>
  );
}

function HRangeDrawing({ drawing, toPixel, offset, isSelected, onClick }) {
  const pts = drawing.points;
  if (pts.length < 2) return null;

  const p1 = toPixel(pts[0]);
  const p2 = toPixel(pts[1]);

  const chartLeft = offset?.left || 0;
  const chartRight = (offset?.left || 0) + (offset?.width || 800);

  const y1 = Math.min(p1.py, p2.py);
  const y2 = Math.max(p1.py, p2.py);
  const h = y2 - y1;

  const topPrice = Math.max(pts[0].y, pts[1].y);
  const botPrice = Math.min(pts[0].y, pts[1].y);

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      {isSelected && <SelectionDefs />}
      <SelectionOutline isSelected={isSelected}>
        <rect
          x={chartLeft} y={y1} width={chartRight - chartLeft} height={h}
          fill={COLORS.hrange.fill}
          stroke={COLORS.hrange.stroke}
          strokeWidth={1}
          strokeDasharray="6 3"
        />
        <text x={chartLeft + 4} y={y1 + 11} fill={COLORS.hrange.stroke} fontSize={9} fontWeight={600} className="select-none">
          {topPrice.toFixed(2)}
        </text>
        <text x={chartLeft + 4} y={y2 - 3} fill={COLORS.hrange.stroke} fontSize={9} fontWeight={600} className="select-none">
          {botPrice.toFixed(2)}
        </text>
      </SelectionOutline>
    </g>
  );
}

function EllipseDrawing({ drawing, toPixel, isSelected, onClick }) {
  const pts = drawing.points;
  if (pts.length < 2) return null;

  const p1 = toPixel(pts[0]);
  const p2 = toPixel(pts[1]);

  const cx = (p1.px + p2.px) / 2;
  const cy = (p1.py + p2.py) / 2;
  const rx = Math.abs(p2.px - p1.px) / 2;
  const ry = Math.abs(p2.py - p1.py) / 2;

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      {isSelected && <SelectionDefs />}
      <SelectionOutline isSelected={isSelected}>
        <ellipse
          cx={cx} cy={cy} rx={rx} ry={ry}
          fill={COLORS.ellipse.fill}
          stroke={COLORS.ellipse.stroke}
          strokeWidth={1.5}
        />
      </SelectionOutline>
    </g>
  );
}

function TextDrawing({ drawing, toPixel, isSelected, onClick }) {
  const pt = drawing.points[0];
  if (!pt) return null;

  const { px, py } = toPixel(pt);
  const label = drawing.meta?.text || 'Note';

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      {isSelected && <SelectionDefs />}
      <SelectionOutline isSelected={isSelected}>
        {/* Background pill */}
        <rect
          x={px - 2} y={py - 12}
          width={label.length * 6.5 + 12} height={18}
          rx={4}
          fill="rgba(255,255,255,0.85)"
          stroke={COLORS.text}
          strokeWidth={0.8}
          className="dark:fill-slate-800/85"
        />
        <text x={px + 4} y={py + 1} fill={COLORS.text} fontSize={10} fontWeight={500} className="select-none">
          {label}
        </text>
      </SelectionOutline>
    </g>
  );
}
